import { PencilIcon, UserIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";
import { Instagram, Linkedin } from "lucide-react"
import { useNavigate } from "react-router-dom";
import AvatarCustomizationModal from "../components/AvatarCustomizationModal.tsx";
import DotsBackground from "../components/DotsBackground.tsx";
import {
  createUserData,
  getSelectedLanguage,
  saveUserData,
} from "../utils/userStorage.ts";

const WelcomePage: React.FC = () => {
  const [username, setUsername] = useState("");
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ username?: string; photo?: string }>(
    {}
  );
  const navigate = useNavigate();

  // Avatar modal state
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [modalAvatar, setModalAvatar] = useState<string | null>(null);
  const [modalBg, setModalBg] = useState<string>("#f3f4f6");

  const validateUsername = (name: string): string | undefined => {
    if (!name.trim()) {
      return "Username tidak boleh kosong";
    }
    if (name.trim().length < 2) {
      return "Username minimal 2 karakter";
    }
    if (name.trim().length > 20) {
      return "Username maksimal 20 karakter";
    }
    if (!/^[a-zA-Z0-9\s]+$/.test(name.trim())) {
      return "Username hanya boleh mengandung huruf, angka, dan spasi";
    }
    return undefined;
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUsername(value);

    // Clear username error on change
    if (errors.username) {
      setErrors((prev) => ({ ...prev, username: undefined }));
    }
  };

  // Open avatar modal for customization
  const openAvatarModal = () => {
    setModalAvatar(profilePhoto);
    setModalBg(profilePhoto && modalBg ? modalBg : "#f3f4f6");
    setShowAvatarModal(true);
  };

  // Save avatar from modal to main profile
  const handleAvatarModalSave = () => {
    setProfilePhoto(modalAvatar);
    setShowAvatarModal(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const usernameError = validateUsername(username);
    const photoError = !profilePhoto ? "Silakan pilih foto profil" : undefined;

    if (usernameError || photoError) {
      setErrors({
        username: usernameError,
        photo: photoError,
      });
      return;
    }

    setIsLoading(true);

    try {
      // Create user data
      const userData = createUserData({
        username: username.trim(),
        photo: profilePhoto!,
        avatarBg: modalBg,
      });

      console.log("👤 Creating user profile:", { username: username.trim() }); // Debug log

      // Save to localStorage
      const success = saveUserData(userData);

      if (success) {
        console.log("💾 User profile saved successfully"); // Debug log

        // Check if user has selected a language
        const selectedLanguage = getSelectedLanguage();
        console.log("🔍 Checking for selected language:", selectedLanguage); // Debug log

        if (selectedLanguage) {
          console.log("🏠 Language already selected, navigating to HomePage"); // Debug log
          navigate("/learn");
        } else {
          console.log("📝 No language selected, navigating to RegisterPage"); // Debug log
          navigate("/register");
        }
      } else {
        setErrors({ photo: "Gagal menyimpan data. Silakan coba lagi." });
      }
    } catch (error) {
      console.error("❌ Error saving user data:", error);
      setErrors({ photo: "Terjadi kesalahan. Silakan coba lagi." });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col relative">
      <DotsBackground />
      {/* Header */}
      <header className="flex items-center justify-between px-8 py-6 relative z-10">
        <div className="flex items-center gap-2">
          <img
            src="../assets/aksa-icon-square.png"
            alt="Aksa Icon"
            className="w-8 h-8"
          />
          <span
            className="text-2xl font-bold tracking-tight"
            style={{ fontFamily: "Feather Bold" }}
          >
            <span style={{ color: "#1A365D" }}>Galaxy</span>{" "}
            <span style={{ color: "#EF4444" }}>Aksara</span>
          </span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center relative z-10 p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md border border-gray-200">
          <div className="text-center mb-8">
            <h1
              className="text-2xl font-bold text-gray-800 mb-2"
              style={{ fontFamily: "Feather Bold" }}
            >
              Selamat Datang!
            </h1>
            <p
              className="text-gray-600"
              style={{ fontFamily: "DIN Next Rounded" }}
            >
              Mari kita siapkan profil Anda untuk memulai belajar Aksara Batak
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Photo Upload (with avatar modal) */}
            <div className="text-center">
              <div
                className="relative inline-block group cursor-pointer"
                onClick={openAvatarModal}
              >
                <div
                  className="w-24 h-24 rounded-full border-4 border-white shadow-lg overflow-hidden flex items-center justify-center"
                  style={{ background: modalBg || "#f3f4f6" }}
                >
                  {profilePhoto ? (
                    <img
                      src={profilePhoto}
                      alt="Profile preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <UserIcon className="w-10 h-10 text-gray-400" />
                  )}
                </div>
                <span className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                  <PencilIcon className="w-7 h-7 text-white" />
                </span>
              </div>
              {errors.photo && (
                <p className="text-red-500 text-sm mt-2">{errors.photo}</p>
              )}
            </div>

            {/* Username Input */}
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Username
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={handleUsernameChange}
                placeholder="Masukkan username Anda"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${errors.username ? "border-red-500" : "border-gray-300"
                  }`}
                style={{ fontFamily: "DIN Next Rounded" }}
              />
              {errors.username && (
                <p className="text-red-500 text-sm mt-1">{errors.username}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 rounded-lg text-white font-bold text-lg transition-all duration-200 transform ${isLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 hover:scale-105"
                }`}
              style={{ fontFamily: "Feather Bold" }}
            >
              {isLoading ? "Menyimpan..." : "Mulai Belajar!"}
            </button>
          </form>

          {/* Local Info */}
          <p
            className="text-center text-xs text-gray-500 mt-6"
            style={{ fontFamily: "DIN Next Rounded" }}
          >
            Data Anda akan disimpan secara lokal di browser ini
          </p>
        </div>
        {/* Avatar Customization Modal */}
        <AvatarCustomizationModal
          show={showAvatarModal}
          onClose={() => setShowAvatarModal(false)}
          modalAvatar={modalAvatar}
          setModalAvatar={setModalAvatar}
          modalBg={modalBg}
          setModalBg={setModalBg}
          onDone={handleAvatarModalSave}
        />
      </main>

      {/* Footer */}
      <footer className="w-full py-6 mt-8 bg-transparent flex flex-col items-center gap-2 z-10 relative">
        <div
          className="text-gray-500 text-sm font-semibold"
          style={{ fontFamily: "DIN Next Rounded" }}
        >
          &copy; 2025 Galaxy Developer
        </div>
        <div className="flex gap-4 mt-1">
          <a
            href="https://www.instagram.com/galaxy.developer/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-gray-500 hover:text-pink-500 transition font-semibold text-sm"
          >
            <Instagram className="text-lg" />
          </a>
          <a
            href="https://www.linkedin.com/company/galaxydeveloper"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-gray-500 hover:text-blue-700 transition font-semibold text-sm"
          >
            <Linkedin className="text-lg" />
          </a>
        </div>
      </footer>
    </div>
  );
};

export default WelcomePage;
